import { useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import { IconPlusInBox } from 'assets/svg/sprite'
import { IconButton } from 'components/IconButton/IconButton'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import { SwitchButton } from 'components-ref/atoms'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import { useModalContext } from 'context/modalContext'
import { AllIcons } from 'helpers/allIcons'
import { type SubscriptionCard as SubscriptionCardType } from 'types/api/subscription'

export const SubscriptionCard = ({
    type,
    icon,
    name,
    subname,
    price,
    discount,
    isActive = false,
    clickFn,
    switchFn,
    editFn,
    deleteFn,
    descritpion,
    selectable = true,
    alwaysExpanded,
    hideArrow,
    customClass = '',
    hideEdit,
    hideToggle,
    monthCount,
    displayMonthlyPrice
}: SubscriptionCardType) => {
    const [expanded, setExpanded] = useState<boolean>(false)
    const cardRef = useRef<HTMLDivElement>(null)

    const modalData = useModalContext()
    const { t } = useTranslation()

    const showToggle = !hideToggle && !!switchFn
    const showEdit = !hideEdit && !!editFn
    const showDelete = !!deleteFn
    const showActions = showToggle || showEdit || showDelete

    const removePlan = () => {
        if (!deleteFn) return

        modalData.addModal(
            t('deletePricingPlan'),
            <ConfirmModal
                body={t('onceYouDeleteThisPricingPlan')}
                confirmFn={() => deleteFn()}
            />
        )
    }

    if (type === 'card') {
        return (
            <div className={`subsOption__card__wrapper ${customClass}`}>
                <div
                    className={`subsOptions__card ${
                        expanded && selectable
                            ? ' subsOptions__card--selected'
                            : ''
                    } ${
                        expanded || alwaysExpanded
                            ? 'subsOptions__card--expanded'
                            : ''
                    }`}
                >
                    <div
                        className='subsOptions__card__head'
                        onClick={() => {
                            setExpanded(!expanded)
                        }}
                    >
                        <div className='subsOptions__card__icon'>
                            {!icon ? (
                                type === 'card' ? (
                                    <img
                                        className='subsOptions__card__icon--plan'
                                        src={AllIcons.payment_sub}
                                        alt={t('plan')}
                                    />
                                ) : (
                                    <img
                                        className='subsOptions__card__icon--add'
                                        src={AllIcons.post_add}
                                        alt={t('add')}
                                    />
                                )
                            ) : typeof icon === 'string' ? (
                                <img
                                    className='subsOptions__card__icon'
                                    src={icon}
                                    alt='icon'
                                />
                            ) : (
                                icon
                            )}
                        </div>

                        <div className='subsOptions__card__info'>
                            {name && typeof name === 'string' ? (
                                <h3 className='subsOptions__card__info__plan'>
                                    {name}
                                </h3>
                            ) : (
                                name
                            )}

                            {subname && subname}

                            <div className='subsOptions__card__info__price'>
                                {price && (
                                    <p>
                                        $
                                        {(Math.round(price * 100) / 100).toFixed(
                                            2
                                        )}
                                    </p>
                                )}

                                {discount && (
                                    <div className='subsOptions__card__info__discount'>
                                        Save {discount}%
                                    </div>
                                )}
                            </div>

                            {displayMonthlyPrice ? (
                                <p className='subsOptions__card__info__monthly'>
                                    $
                                    {price &&
                                        monthCount &&
                                        (price / monthCount).toFixed(2)}{' '}
                                    / {t('month')}
                                </p>
                            ) : (
                                ''
                            )}
                        </div>

                        {!hideArrow && (
                            <div className='subsOptions__card__buttons'>
                                <div
                                    className={`subsOptions__card__buttons__chevron${
                                        expanded
                                            ? ' subsOptions__card__buttons__chevron--active'
                                            : ''
                                    }`}
                                >
                                    <img
                                        src={AllIcons.chevron_down}
                                        alt={t('icon')}
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    <div
                        ref={cardRef}
                        className={`subsOptions__card__body${
                            expanded || alwaysExpanded
                                ? ' subsOptions__card__body--active'
                                : ''
                        }`}
                    >
                        {descritpion && descritpion}

                        {showActions && (
                            <div className='subsOptions__actions'>
                                {showToggle && (
                                    <SwitchButton
                                        active={isActive}
                                        toggle={() => {
                                            switchFn?.()
                                        }}
                                    />
                                )}

                                {(showDelete || showEdit) && (
                                    <div
                                        className={`subsOptions__actions__buttons ${
                                            !showToggle
                                                ? 'subsOptions__actions__buttons-toggleHidden'
                                                : ''
                                        }`}
                                    >
                                        {showDelete && (
                                            <IconButton
                                                icon={AllIcons.chat_delete}
                                                type='white'
                                                customClass='subsOptions__actions__button'
                                                clickFn={removePlan}
                                            />
                                        )}

                                        {showEdit && (
                                            <IconButton
                                                icon={AllIcons.post_edit}
                                                type='white'
                                                customClass='subsOptions__actions__button'
                                                clickFn={() => editFn?.()}
                                            />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {expanded && selectable ? (
                    <div className='subsOptions__card__selected'>
                        <img
                            src={AllIcons.checkmark_blue}
                            alt={t('selected')}
                        />
                    </div>
                ) : (
                    ''
                )}
            </div>
        )
    }

    return (
        <ActionCard
            icon={<IconPlusInBox width='22' height='22' />}
            iconSize='big'
            text={name ?? t('addNewPricing')}
            addNewText
            clickFn={() => clickFn?.()}
        />
    )
}